import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { Icon } from './icons.types';
import { IconsService } from './iconspicker-dialog.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'iconpicker-dialog',
    templateUrl: './iconpicker-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .iconpicker-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IconPickerDialogComponent implements OnInit, OnDestroy {

    @ViewChild('iconsWrapper') iconsWrapper: ElementRef;

    icons$: Observable<Icon[]>;
    nameSpaces$: { namespace: string; name: string }[];
    filteredIcons$: Observable<Icon[]>;
    filterValue$ = new BehaviorSubject('');
    filterNameValue$ = new BehaviorSubject('');

    familyFilterControl = new FormControl('');

    iconSize = 'icon-size-8';
    selectedIcon: string[] = [];

    loading = true;

    private _unsubscribeAll = new Subject();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            svgIcon: string;
            optionNameSpaces: string[];
        },
        public dialogRef: MatDialogRef<IconPickerDialogComponent>,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private _iconsService: IconsService
    ) { }

    ngOnInit(): void {
        //Get icons
        this.loading = true;
        this.icons$ = this._iconsService.getIcons(this.data?.optionNameSpaces);
        this.nameSpaces$ = this._iconsService.nameSpaces.filter(item => this.data?.optionNameSpaces.includes(item.namespace));
        this.familyFilterControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => this.filterNamespace(value));

        // Subscribe to icons
        this.icons$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((icons) => {

                this.calcIconFromSvg();

                // Set the icon size in case the icon set
                // has a special base grid size
                this.iconSize = icons[0].grid;

                // Select the first icon
                if (!this.selectedIcon?.[1]) {
                    this.selectedIcon = [icons[0].namespace, icons[0].list[0]];
                }
                this.loading = false;
            });


        // Create filtered icons
        this.filteredIcons$ = combineLatest([this.icons$, this.filterValue$, this.filterNameValue$])
            .pipe(
                map(([icons, filterValue, filterNameValue]) => {
                    this.loading = true;
                    const filteredNames = !filterNameValue ? icons : icons.filter(icon => icon.name.includes(filterNameValue));

                    // Filter the icons
                    const filteredIcons: Icon[] = [];
                    for (const item of filteredNames) {
                        const check = item.list.filter(iconName => iconName.toLowerCase().includes(filterValue.toLowerCase()));
                        filteredIcons.push({ namespace: item.namespace, name: item.name, grid: item.grid, list: check });
                    }

                    // Update the list with the filtered icons
                    this.loading = false;
                    return filteredIcons;
                }),
                takeUntil(this._unsubscribeAll)
            );
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Filter icons
     *
     * @param event
     */
    filterIcons(event: any): void {
        // Push the value to the observable
        this.filterValue$.next(event.target.value);
        this.iconsWrapper.nativeElement.scrollTo(0, 0);
    }

    filterNamespace(value: string): void {
        // Push the value to the observable
        this.filterNameValue$.next(value);
        this.iconsWrapper.nativeElement.scrollTo(0, 0);
    }

    /**
     * Select an icon
     *
     * @param namespace
     * @param icon
     */
    selectIcon(namespace: string, icon: string): void {
        this.selectedIcon = [
            namespace,
            icon
        ];
    }

    /**
     * Returns the selected icon's svgIcon
     * to use in mat-icon component
     */
    calcSvgIconAttr(): string {
        if (!this.selectedIcon) {
            return '';
        }

        if (this.selectedIcon[0] === '') {
            return this.selectedIcon[1];
        }

        return this.selectedIcon.join(':');
    }

    calcIconFromSvg(): void {
        if (this.data?.svgIcon) {
            if (this.data?.svgIcon.includes(':')) {
                this.selectedIcon = this.data?.svgIcon.split(':');
            } else {
                this.selectedIcon[0] = '';
                this.selectedIcon[1] = this.data?.svgIcon;
            }
        }
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('saveDataItemConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.closeDialog(this.calcSvgIconAttr());
            }
        });
    }

    closeDialog(icon?: string) {
        this.dialogRef.close(icon);
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
